import React from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Checkbox,
    Column,
    Columns,
    Divider,
    Dropdown,
    Drawer,
    Stack,
    Text,
    TextField,
} from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import { getAllVendors } from "../../services/vendorService";
import NumericTextField from "../NumericTextField";

export interface VendorFormData {
    ven_accno: string;
    ven_code: string;
    ven_Name: string;
    ven_titile: string;
    ven_groupid: string;
    ven_active: boolean;
    ven_add1: string;
    ven_add2: string;
    ven_add3: string;
    ven_add4: string;
    ven_tep1: string;
    ven_tep2: string;
    ven_email: string;
    ven_contact: string;
    ven_reordercycle: number | null;
    ven_creditlimit: number | null;
    ven_creditperiod: number | null;
    ven_opbal: number | null;
    ven_purchase: number | null;
    ven_returns: number | null;
    ven_creditnote: number | null;
    ven_debitnote: number | null;
    ven_payment: number | null;
    ven_balance: number | null;
    ven_taxregno: string;
    ven_latitude: string;
    ven_longitude: string;
}

export type VendorToEdit = {
    ven_id?: number | null;
} & Partial<{
    [K in keyof VendorFormData]: VendorFormData[K] | null;
}>;

interface FormErrors {
    ven_accno?: string;
    ven_code?: string;
    ven_Name?: string;
    ven_titile?: string;
    ven_groupid?: string;
    ven_add1?: string;
    ven_add2?: string;
    ven_add3?: string;
    ven_add4?: string;
    ven_tep1?: string;
    ven_tep2?: string;
    ven_email?: string;
    ven_contact?: string;
    ven_reordercycle?: string;
    ven_creditlimit?: string;
    ven_creditperiod?: string;
    ven_opbal?: string;
    ven_purchase?: string;
    ven_returns?: string;
    ven_creditnote?: string;
    ven_debitnote?: string;
    ven_payment?: string;
    ven_balance?: string;
    ven_taxregno?: string;
    ven_latitude?: string;
    ven_longitude?: string;
}

interface CreateUpdateVendorDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    vendorToEdit?: VendorToEdit;
    onSave: (vendorData: VendorFormData, editingVendor?: VendorToEdit) => Promise<void> | void;
    onVendorSaved?: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: VendorFormData = {
    ven_accno: "",
    ven_code: "",
    ven_Name: "",
    ven_titile: "",
    ven_groupid: "",
    ven_active: true,
    ven_add1: "",
    ven_add2: "",
    ven_add3: "",
    ven_add4: "",
    ven_tep1: "",
    ven_tep2: "",
    ven_email: "",
    ven_contact: "",
    ven_reordercycle: null,
    ven_creditlimit: null,
    ven_creditperiod: null,
    ven_opbal: null,
    ven_purchase: null,
    ven_returns: null,
    ven_creditnote: null,
    ven_debitnote: null,
    ven_payment: null,
    ven_balance: null,
    ven_taxregno: "",
    ven_latitude: "",
    ven_longitude: "",
};

const CreateUpdateVendorDrawer: React.FC<CreateUpdateVendorDrawerProps> = ({
    isOpen,
    onClose,
    vendorToEdit,
    onSave,
    onVendorSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [formData, setFormData] = React.useState<VendorFormData>(defaultFormData);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState(false);
    const isEditing = Boolean(vendorToEdit);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (vendorToEdit) {
            setFormData({
                ven_accno: vendorToEdit.ven_accno ?? "",
                ven_code: vendorToEdit.ven_code ?? "",
                ven_Name: vendorToEdit.ven_Name ?? "",
                ven_titile: vendorToEdit.ven_titile ?? "",
                ven_groupid: vendorToEdit.ven_groupid ?? "",
                ven_active: vendorToEdit.ven_active ?? true,
                ven_add1: vendorToEdit.ven_add1 ?? "",
                ven_add2: vendorToEdit.ven_add2 ?? "",
                ven_add3: vendorToEdit.ven_add3 ?? "",
                ven_add4: vendorToEdit.ven_add4 ?? "",
                ven_tep1: vendorToEdit.ven_tep1 ?? "",
                ven_tep2: vendorToEdit.ven_tep2 ?? "",
                ven_email: vendorToEdit.ven_email ?? "",
                ven_contact: vendorToEdit.ven_contact ?? "",
                ven_reordercycle: vendorToEdit.ven_reordercycle ?? null,
                ven_creditlimit: vendorToEdit.ven_creditlimit ?? null,
                ven_creditperiod: vendorToEdit.ven_creditperiod ?? null,
                ven_opbal: vendorToEdit.ven_opbal ?? null,
                ven_purchase: vendorToEdit.ven_purchase ?? null,
                ven_returns: vendorToEdit.ven_returns ?? null,
                ven_creditnote: vendorToEdit.ven_creditnote ?? null,
                ven_debitnote: vendorToEdit.ven_debitnote ?? null,
                ven_payment: vendorToEdit.ven_payment ?? null,
                ven_balance: vendorToEdit.ven_balance ?? null,
                ven_taxregno: vendorToEdit.ven_taxregno ?? "",
                ven_latitude: vendorToEdit.ven_latitude ?? "",
                ven_longitude: vendorToEdit.ven_longitude ?? "",
            });
        } else {
            setFormData(defaultFormData);
        }

        setErrors({});
    }, [isOpen, vendorToEdit]);

    const clearError = (key: keyof FormErrors) => {
        if (errors[key]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const updateText = (key: keyof VendorFormData, value: string, maxLength?: number) => {
        const normalizedValue = typeof maxLength === "number" ? value.slice(0, maxLength) : value;
        setFormData((prev) => ({ ...prev, [key]: normalizedValue }));
        clearError(key as keyof FormErrors);
    };

    const updateNumber = (key: keyof VendorFormData, value: string) => {
        const parsedValue = value === "" || value === "-" ? null : Number(value);
        setFormData((prev) => ({ ...prev, [key]: parsedValue }));
        clearError(key as keyof FormErrors);
    };

    const flagNumbersOnly = (key: keyof FormErrors) => () => {
        setErrors((prev) => ({
            ...prev,
            [key]: t("page.vendor_management.drawer.validation.numbers_only", {
                defaultValue: "Please enter numbers only",
            }),
        }));
    };

    const updateBoolean = (key: keyof VendorFormData, value: boolean) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        const checkMaxLength = (key: keyof FormErrors, value: string | null | undefined, max: number) => {
            if ((value || "").length > max) {
                nextErrors[key] = t("page.vendor_management.drawer.validation.max_length", {
                    defaultValue: "Maximum {{count}} characters allowed",
                    count: max,
                });
            }
        };

        const checkNumeric = (key: keyof FormErrors, value: number | null) => {
            if (value === null) {
                return;
            }

            if (!Number.isFinite(value)) {
                nextErrors[key] = t("page.vendor_management.drawer.validation.invalid_number", {
                    defaultValue: "Invalid numeric value",
                });
            }
        };

        const checkNumericMaxLength = (key: keyof FormErrors, value: number | null, max = 21) => {
            if (value === null || value === undefined) {
                return;
            }

            if (String(value).length > max) {
                nextErrors[key] = t("page.vendor_management.drawer.validation.max_length", {
                    defaultValue: "Maximum {{count}} characters allowed",
                    count: max,
                });
            }
        };

        if (!(formData.ven_code ?? "").trim()) {
            nextErrors.ven_code = t("page.vendor_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (!(formData.ven_Name ?? "").trim()) {
            nextErrors.ven_Name = t("page.vendor_management.drawer.validation.required", { defaultValue: "Required" });
        }

        checkMaxLength("ven_code", formData.ven_code, 32);
        checkMaxLength("ven_Name", formData.ven_Name, 60);
        checkMaxLength("ven_titile", formData.ven_titile, 10);
        checkMaxLength("ven_groupid", formData.ven_groupid, 5);
        checkMaxLength("ven_add1", formData.ven_add1, 40);
        checkMaxLength("ven_add2", formData.ven_add2, 40);
        checkMaxLength("ven_add3", formData.ven_add3, 40);
        checkMaxLength("ven_add4", formData.ven_add4, 40);
        checkMaxLength("ven_tep1", formData.ven_tep1, 30);
        checkMaxLength("ven_tep2", formData.ven_tep2, 30);
        checkMaxLength("ven_email", formData.ven_email, 40);
        checkMaxLength("ven_contact", formData.ven_contact, 40);
        checkMaxLength("ven_taxregno", formData.ven_taxregno, 50);
        checkMaxLength("ven_latitude", formData.ven_latitude, 50);
        checkMaxLength("ven_longitude", formData.ven_longitude, 50);

        if ((formData.ven_email ?? "").trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test((formData.ven_email ?? "").trim())) {
                nextErrors.ven_email = t("page.vendor_management.drawer.validation.invalid_email", {
                    defaultValue: "Invalid email address",
                });
            }
        }

        [
            ["ven_reordercycle", formData.ven_reordercycle],
            ["ven_creditlimit", formData.ven_creditlimit],
            ["ven_creditperiod", formData.ven_creditperiod],
            ["ven_opbal", formData.ven_opbal],
            ["ven_purchase", formData.ven_purchase],
            ["ven_returns", formData.ven_returns],
            ["ven_creditnote", formData.ven_creditnote],
            ["ven_debitnote", formData.ven_debitnote],
            ["ven_payment", formData.ven_payment],
            ["ven_balance", formData.ven_balance],
        ].forEach(([key, value]) => {
            checkNumeric(key as keyof FormErrors, value as number | null);
            checkNumericMaxLength(key as keyof FormErrors, value as number | null, 21);
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isReadOnly) {
            return;
        }

        if (submitting) {
            return;
        }

        if (!validate()) {
            toast.showError(
                t("page.vendor_management.drawer.validation.fix_errors", {
                    defaultValue: "Please fix validation errors",
                }),
            );
            return;
        }

        // Check for duplicate vendor code
        if ((formData.ven_code ?? "").trim()) {
            try {
                const existingVendors = await getAllVendors();
                const duplicate = existingVendors.find(
                    (v) =>
                        (v.ven_code || "").trim().toUpperCase() === (formData.ven_code ?? "").trim().toUpperCase() &&
                        v.ven_id !== vendorToEdit?.ven_id
                );
                if (duplicate) {
                    setErrors((prev) => ({
                        ...prev,
                        ven_code: t("page.vendor_management.drawer.validation.duplicate_code", {
                            defaultValue: "This vendor code already exists",
                        }),
                    }));
                    toast.showError(
                        t("page.vendor_management.drawer.validation.duplicate_code", {
                            defaultValue: "This vendor code already exists",
                        })
                    );
                    return;
                }
            } catch (error: any) {
                toast.showError(error?.message || "Failed to validate vendor code");
                return;
            }
        }

        try {
            setSubmitting(true);
            await onSave(formData, vendorToEdit);

            if (onVendorSaved) {
                onVendorSaved();
            }

            toast.showSuccess(
                vendorToEdit
                    ? t("page.vendor_management.drawer.success.updated", { defaultValue: "Vendor updated successfully" })
                    : t("page.vendor_management.drawer.success.created", { defaultValue: "Vendor created successfully" }),
            );

            onClose();
        } catch (error: any) {
            toast.showError(
                error?.message || t("page.vendor_management.drawer.error.save_failed", { defaultValue: "Failed to save vendor" }),
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={vendorToEdit ? t("page.vendor_management.drawer.title.update", { defaultValue: "Update Vendor" }) : t("page.vendor_management.drawer.title.create", { defaultValue: "Create New Vendor" })}
            width="medium"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        <Text weight="strong" tone="info">{t("page.vendor_management.drawer.section.basic", { defaultValue: "Basic Details" })}</Text>

                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <TextField label={t("page.vendor_management.drawer.label.ven_accno", { defaultValue: "Account Number" })} value={formData.ven_accno} tone={errors.ven_accno ? "critical" : undefined} message={errors.ven_accno} onChange={(e) => updateText("ven_accno", e.currentTarget.value, 20)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_code", { defaultValue: "Vendor Code*" })} value={formData.ven_code} tone={errors.ven_code ? "critical" : undefined} message={errors.ven_code} onChange={(e) => updateText("ven_code", e.currentTarget.value, 32)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_name", { defaultValue: "Vendor Name*" })} value={formData.ven_Name} tone={errors.ven_Name ? "critical" : undefined} message={errors.ven_Name} onChange={(e) => updateText("ven_Name", e.currentTarget.value, 60)} />
                                    <Dropdown
                                        label={t("page.vendor_management.drawer.label.ven_titile", { defaultValue: "Title" })}
                                        value={formData.ven_titile}
                                        tone={errors.ven_titile ? "critical" : undefined}
                                        message={errors.ven_titile}
                                        onChange={(e) => updateText("ven_titile", e.currentTarget.value, 10)}
                                    >
                                        <option value="">{t("page.vendor_management.drawer.title.select", { defaultValue: "Select Title" })}</option>
                                        <option value="Mr.">Mr.</option>
                                        <option value="Miss">Miss</option>
                                        <option value="Mrs.">Mrs.</option>
                                        <option value="Dr.">Dr.</option>
                                    </Dropdown>
                                    <TextField label={t("page.vendor_management.drawer.label.ven_groupid", { defaultValue: "Group ID" })} value={formData.ven_groupid} tone={errors.ven_groupid ? "critical" : undefined} message={errors.ven_groupid} onChange={(e) => updateText("ven_groupid", e.currentTarget.value, 5)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_email", { defaultValue: "Email" })} value={formData.ven_email} tone={errors.ven_email ? "critical" : undefined} message={errors.ven_email} onChange={(e) => updateText("ven_email", e.currentTarget.value, 40)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_contact", { defaultValue: "Contact" })} value={formData.ven_contact} tone={errors.ven_contact ? "critical" : undefined} message={errors.ven_contact} onChange={(e) => updateText("ven_contact", e.currentTarget.value, 40)} />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="small">
                                    <TextField label={t("page.vendor_management.drawer.label.ven_tep1", { defaultValue: "Telephone 1" })} value={formData.ven_tep1} tone={errors.ven_tep1 ? "critical" : undefined} message={errors.ven_tep1} onChange={(e) => updateText("ven_tep1", e.currentTarget.value, 30)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_tep2", { defaultValue: "Telephone 2" })} value={formData.ven_tep2} tone={errors.ven_tep2 ? "critical" : undefined} message={errors.ven_tep2} onChange={(e) => updateText("ven_tep2", e.currentTarget.value, 30)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_add1", { defaultValue: "Address Line 1" })} value={formData.ven_add1} tone={errors.ven_add1 ? "critical" : undefined} message={errors.ven_add1} onChange={(e) => updateText("ven_add1", e.currentTarget.value, 40)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_add2", { defaultValue: "Address Line 2" })} value={formData.ven_add2} tone={errors.ven_add2 ? "critical" : undefined} message={errors.ven_add2} onChange={(e) => updateText("ven_add2", e.currentTarget.value, 40)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_add3", { defaultValue: "Address Line 3" })} value={formData.ven_add3} tone={errors.ven_add3 ? "critical" : undefined} message={errors.ven_add3} onChange={(e) => updateText("ven_add3", e.currentTarget.value, 40)} />
                                    <TextField label={t("page.vendor_management.drawer.label.ven_add4", { defaultValue: "Address Line 4" })} value={formData.ven_add4} tone={errors.ven_add4 ? "critical" : undefined} message={errors.ven_add4} onChange={(e) => updateText("ven_add4", e.currentTarget.value, 40)} />
                                </Stack>
                            </Column>
                        </Columns>

                        <Divider />

                        <Text weight="strong" tone="info">{t("page.vendor_management.drawer.section.financial", { defaultValue: "Financial Details" })}</Text>

                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_reordercycle", { defaultValue: "Reorder Cycle" })} value={formData.ven_reordercycle ?? ""} tone={errors.ven_reordercycle ? "critical" : undefined} message={errors.ven_reordercycle} onChange={(value) => updateNumber("ven_reordercycle", value)} onInvalid={flagNumbersOnly("ven_reordercycle")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_creditlimit", { defaultValue: "Credit Limit" })} value={formData.ven_creditlimit ?? ""} tone={errors.ven_creditlimit ? "critical" : undefined} message={errors.ven_creditlimit} onChange={(value) => updateNumber("ven_creditlimit", value)} onInvalid={flagNumbersOnly("ven_creditlimit")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_creditperiod", { defaultValue: "Credit Period" })} value={formData.ven_creditperiod ?? ""} tone={errors.ven_creditperiod ? "critical" : undefined} message={errors.ven_creditperiod} onChange={(value) => updateNumber("ven_creditperiod", value)} onInvalid={flagNumbersOnly("ven_creditperiod")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_opbal", { defaultValue: "Opening Balance" })} value={formData.ven_opbal ?? ""} disabled={isEditing} tone={errors.ven_opbal ? "critical" : undefined} message={errors.ven_opbal} onChange={(value) => updateNumber("ven_opbal", value)} onInvalid={flagNumbersOnly("ven_opbal")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_purchase", { defaultValue: "Purchase" })} value={formData.ven_purchase ?? ""} tone={errors.ven_purchase ? "critical" : undefined} message={errors.ven_purchase} onChange={(value) => updateNumber("ven_purchase", value)} onInvalid={flagNumbersOnly("ven_purchase")} />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="small">
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_returns", { defaultValue: "Returns" })} value={formData.ven_returns ?? ""} tone={errors.ven_returns ? "critical" : undefined} message={errors.ven_returns} onChange={(value) => updateNumber("ven_returns", value)} onInvalid={flagNumbersOnly("ven_returns")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_creditnote", { defaultValue: "Credit Note" })} value={formData.ven_creditnote ?? ""} tone={errors.ven_creditnote ? "critical" : undefined} message={errors.ven_creditnote} onChange={(value) => updateNumber("ven_creditnote", value)} onInvalid={flagNumbersOnly("ven_creditnote")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_debitnote", { defaultValue: "Debit Note" })} value={formData.ven_debitnote ?? ""} tone={errors.ven_debitnote ? "critical" : undefined} message={errors.ven_debitnote} onChange={(value) => updateNumber("ven_debitnote", value)} onInvalid={flagNumbersOnly("ven_debitnote")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_payment", { defaultValue: "Payment" })} value={formData.ven_payment ?? ""} tone={errors.ven_payment ? "critical" : undefined} message={errors.ven_payment} onChange={(value) => updateNumber("ven_payment", value)} onInvalid={flagNumbersOnly("ven_payment")} />
                                    <NumericTextField label={t("page.vendor_management.drawer.label.ven_balance", { defaultValue: "Balance" })} value={formData.ven_balance ?? ""} disabled={isEditing} allowNegative tone={errors.ven_balance ? "critical" : undefined} message={errors.ven_balance} onChange={(value) => updateNumber("ven_balance", value)} onInvalid={flagNumbersOnly("ven_balance")} />
                                </Stack>
                            </Column>
                        </Columns>

                        <Divider />

                        <Text weight="strong" tone="info">{t("page.vendor_management.drawer.section.location", { defaultValue: "Tax And Location" })}</Text>

                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/3">
                                <TextField label={t("page.vendor_management.drawer.label.ven_taxregno", { defaultValue: "Tax Reg No" })} value={formData.ven_taxregno} tone={errors.ven_taxregno ? "critical" : undefined} message={errors.ven_taxregno} onChange={(e) => updateText("ven_taxregno", e.currentTarget.value, 50)} />
                            </Column>
                            <Column width="1/3">
                                <TextField label={t("page.vendor_management.drawer.label.ven_latitude", { defaultValue: "Latitude" })} value={formData.ven_latitude} tone={errors.ven_latitude ? "critical" : undefined} message={errors.ven_latitude} onChange={(e) => updateText("ven_latitude", e.currentTarget.value, 50)} />
                            </Column>
                            <Column width="1/3">
                                <TextField label={t("page.vendor_management.drawer.label.ven_longitude", { defaultValue: "Longitude" })} value={formData.ven_longitude} tone={errors.ven_longitude ? "critical" : undefined} message={errors.ven_longitude} onChange={(e) => updateText("ven_longitude", e.currentTarget.value, 50)} />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Checkbox id="vendor-active" label={t("page.vendor_management.drawer.label.ven_active", { defaultValue: "Vendor Is Active" })} checked={formData.ven_active} onChange={(e) => updateBoolean("ven_active", e.currentTarget.checked)} />
                            </Column>
                        </Columns>

                        <Actions>
                            <Button variant="ghost" tone="critical" onClick={onClose}>{t("page.vendor_management.drawer.button.cancel", { defaultValue: "Cancel" })}</Button>
                            <Button variant="solid" type={isReadOnly ? "button" : "submit"} tone="brandAccent">
                                {isReadOnly
                                    ? t("page.vendor_management.drawer.button.view_only", { defaultValue: "View Only" })
                                    : submitting
                                    ? t("page.vendor_management.drawer.button.saving", { defaultValue: "Saving..." })
                                    : vendorToEdit
                                      ? t("page.vendor_management.drawer.button.update", { defaultValue: "Update Vendor" })
                                      : t("page.vendor_management.drawer.button.create", { defaultValue: "Create Vendor" })}
                            </Button>
                        </Actions>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default CreateUpdateVendorDrawer;
